import { useQuery } from "@tanstack/react-query";
import { axios } from "./axios";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ROUTE } from "../routes";
import { env } from "../utils/env";
import { AccessRoleDetail } from "../types/AccessRole";

const fetchAccessRole = (authHeader: string, id: number | string) => () =>
  axios.get<AccessRoleDetail>(`${env.verifyServiceUrl}user/access_role/${id}/`, {
    headers: {
      Authorization: authHeader,
    },
  });

/**
 * Single-Role detail — GET `/user/access_role/{id}/`. Unlike the tenant-wide
 * list (`useAccessRoles`), this is the Role's own record, so it's used as the
 * source of truth for which permissions and users the edit wizard should
 * show as already toggled on — rather than the permission catalogue's
 * unconfirmed `?role=` filter (see `Permission.is_granted`) or a separate
 * `/user/user_role/` list.
 *
 * `enabled` deliberately doesn't gate on `typeof id === "number"` — `strict:
 * false` means `AccessRole.id`'s `number` type isn't a runtime guarantee, and
 * a role id that actually arrives as a numeric string would silently and
 * permanently disable this query under that check (the query never fires,
 * no error, nothing to notice). Accepting `number | string` and checking for
 * "present" instead avoids that failure mode.
 */
export const useAccessRole = (id?: number | string) => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();

  return useQuery(
    ["accessRole", id ?? null],
    fetchAccessRole(authHeader, id as number | string),
    {
      enabled: id !== undefined && id !== null && id !== "",
      retry: false,
      onError: (err: any) => {
        if (err.request?.status === 500 || err.request?.status === 503) {
          navigate(ROUTE.SERVER_ERROR);
        }
        if (err.request?.status === 401) {
          navigate(ROUTE.LOGOUT);
        }
      },
    }
  );
};
